// @flow
import { CommonActions } from '@react-navigation/native';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';
import { tripica } from '../../api';
import HandleErrorService from '../../lib/handleError';
import Navigation from '../../lib/navigation';
import { customerOuidSelector, customerTaxExemptionsSelector } from '../Customer/selectors';
import {
  currentSubscriptionBillingAccountOuidSelector,
  currentSubscriptionLineSelector,
  currentSubscriptionOuidSelector,
} from '../Dashboard';
import { setLoading } from '../LoadingStatus/actions';
import { triggerPaymentProductOrderPolling } from '../PollingOrder';
import { voucherCurrentVoucherDetailsSelector } from '../Voucher';
import type { TopUpsAction } from './actions';
import { setAvailablePermanentTopUps, setAvailableTopUps } from './actions';
import { selectedTopUpsSelector } from './selectors';
import WizzeeLog from '../../lib/wizzeeLog';

export function* topUpUsages(action: TopUpsAction): Generator<*, *, *> {
  if (action.type !== 'TOP_UP_USAGES') return; // for flow
  const customerOuid = yield select(customerOuidSelector);
  const subscriptionOuid = yield select(currentSubscriptionOuidSelector);
  const billingAccountOuid = yield select(currentSubscriptionBillingAccountOuidSelector);
  const topUps = yield select(selectedTopUpsSelector);
  const taxExemptions = yield select(customerTaxExemptionsSelector);

  if (!topUps) return;

  // Handling voucher if any selected
  const voucherDetails = yield select(voucherCurrentVoucherDetailsSelector);

  try {
    yield put(setLoading('topUpConfirmation', true));
    const response = yield call(
      [tripica, 'topUp'],
      customerOuid,
      subscriptionOuid,
      billingAccountOuid,
      topUps,
      taxExemptions,
      undefined,
      voucherDetails
    );

    if (!response || !response.errorCodeList) {
      throw new Error('No response or errorCodeList');
    }

    if (response.status === 'VALIDATED') {
      const productOrder = yield call(triggerPaymentProductOrderPolling, 'TOP_UP', response.uuid);

      if (productOrder.state === 'COMPLETED') {
        yield call(Navigation.dispatch, CommonActions.navigate({ name: 'PaymentSuccess' }));
      } else if (productOrder.state === 'IN_PROGRESS') {
        yield call(Navigation.dispatch, CommonActions.navigate({ name: 'PaymentInProgress' }));
      } else {
        throw new Error('product order fetch failed');
      }
    } else if (response.errorCodeList.includes('RENEWAL_IN_PROGRESS')) {
      yield call(Navigation.dispatch, CommonActions.navigate({ name: 'PaymentRenewalInProgress' }));
    } else {
      throw new Error(response.errorCodeList.join(','));
    }
  } catch (e) {
    WizzeeLog.warn('[saga] topUpUsages', e);
    yield call(Navigation.dispatch, CommonActions.navigate({ name: 'PaymentFailure' }));
  } finally {
    yield put(setLoading('topUpConfirmation', false));
  }
}

export function* fetchTopUpsSaga(action: TopUpsAction): Generator<*, *, *> {
  try {
    yield put(setLoading('fetchTopUps', true));

    // $FlowFixMe
    const { permanent, topUpType } = action.payload;
    const line = yield select(currentSubscriptionLineSelector);
    if (!line) return;

    let onSuccess, topUps;
    if (permanent) {
      onSuccess = setAvailablePermanentTopUps;
      topUps = yield call([tripica, 'getAvailablePermanentTopUps'], line.number);

      yield put(onSuccess(topUps));
    } else if (topUpType) {
      onSuccess = setAvailableTopUps;
      topUps = yield call([tripica, 'getAvailableTopUpsForType'], line.number, topUpType);

      yield put(onSuccess(topUps, topUpType));
    } else {
      onSuccess = setAvailableTopUps;
      topUps = yield call([tripica, 'getAvailableTopUps'], line.number);

      yield put(onSuccess(topUps, 'data'));
    }
  } catch (e) {
    WizzeeLog.warn('[saga] fetchTopUps');
    HandleErrorService.showToastError(e);
  } finally {
    yield put(setLoading('fetchTopUps', false));
  }
}

export default function*(): Generator<*, *, *> {
  yield all([takeEvery('TOP_UP_USAGES', topUpUsages), takeEvery('FETCH_TOP_UPS', fetchTopUpsSaga)]);
}
